import React, { useMemo } from 'react';
import { ST_BUDGET_FIELDS } from '../../growth-settings/mockData';
import type { StBudgetFieldId } from '../../growth-settings/types';
import { StFlatPanel, StSectionCard, type StSectionSaveFooter } from './SettingsPrimitives';

export function SettingsBudget({
  values,
  onChange,
  saveFooter,
}: {
  values: Record<StBudgetFieldId, string>;
  onChange: (id: StBudgetFieldId, value: string) => void;
  saveFooter?: StSectionSaveFooter;
}) {
  const summaryValues = useMemo(
    () => [
      { label: 'MONTHLY LIMIT', value: `$${Number(values['monthly-max']).toLocaleString()}` },
      { label: 'DAILY LIMIT', value: `$${Number(values['daily-cap']).toLocaleString()}` },
      { label: 'MAX PER CAMPAIGN', value: `$${Number(values['campaign-max']).toLocaleString()}` },
      {
        label: 'DAILY ALERT IF OVER',
        value: `$${Number(values['alert-threshold']).toLocaleString()}`,
      },
    ],
    [values],
  );

  return (
    <StSectionCard
      title="Budget & Guardrails"
      description="Set spending limits to make sure ELVA never exceeds your comfort zone. These apply across all campaigns."
      {...(saveFooter ? { saveFooter } : {})}
    >
      <section className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {ST_BUDGET_FIELDS.map((field) => (
          <label key={field.id} className="block">
            <span className="text-[13.5px] font-medium text-[var(--ink-1)]">{field.label}</span>
            <span className="mt-1 block text-[13px] leading-normal text-[var(--ink-3)]">
              {field.hint}
            </span>
            <span className="mt-2 flex h-11 items-center gap-2 rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4">
              <span className="text-[15px] text-[var(--ink-4)]">$</span>
              <input
                value={values[field.id]}
                onChange={(event) =>
                  onChange(field.id, event.target.value.replace(/[^\d]/g, ''))
                }
                inputMode="numeric"
                className="h-full min-w-0 flex-1 border-none bg-transparent text-[15px] font-medium text-[var(--ink-1)] outline-none"
              />
              <span className="text-[13px] text-[var(--ink-4)]">{field.suffix}</span>
            </span>
          </label>
        ))}
      </section>

      <StFlatPanel>
        <h3 className="m-0 font-[var(--font-display)] text-[15px] font-bold leading-snug tracking-[-0.02em] text-[var(--ink-1)]">
          Current Guardrails Summary
        </h3>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 xl:gap-0">
          {summaryValues.map((item, index) => (
            <div
              key={item.label}
              className={[
                index < summaryValues.length - 1 ? 'xl:border-r xl:border-[var(--cream-2)] xl:pr-6' : '',
                index > 0 ? 'xl:pl-6' : '',
              ].join(' ')}
            >
              <p className="text-[11px] font-semibold tracking-[0.08em] text-[var(--ink-4)]">
                {item.label}
              </p>
              <p
                className="mt-2 text-[24px] font-semibold leading-none"
                style={{
                  color:
                    index === summaryValues.length - 1 ? 'var(--gold)' : 'var(--ink-1)',
                }}
              >
                {item.value}
              </p>
            </div>
          ))}
        </div>
      </StFlatPanel>
    </StSectionCard>
  );
}
